import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'

import { OrderDto, OrderLineDto } from '../../../types/swaggerTypes'
import {
  StyledActions,
  StyledProductDetailsSection
} from './ReassignedProducts.styles'
import { ClinText } from '../../../components/ClinText'
import { ClinTheme } from '../../../ClinTheme'
import {
  FixedWidthCell,
  ProductInformationBox,
  StyledClinPatientTableHeaderCell,
  StyledClinTable,
  StyledClinTableBodyCell,
  StyledClinTableExpandedRow,
  StyledClinTableRow,
  StyledClinTableToggleRowButton,
  StyledDesktopWrapper,
  StyledProductInformationBox,
  StyledStatusCell,
  StyledStrengthCell
} from '../../OrderPages/OrderDetail/OrderProductDetails.styles'
import { StyledClinTableOrderToggleWrapper } from '../../Patients/PatientDashboard/PatientDashboard.styles'
import { ClinTableOrderToggle } from '../../../components/ClinTableOrderToggle'
import ProductInformation from '../ProductInformation/ProductInformation'
import { ClinTableBodyCell } from '../../../components/ClinTable/ClinTable'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import StatusColumn from '../../OrderPages/Orders/StatusColumn/StatusColumn'
import { getReassignedOrderItems } from '../../OpaCheckout/OpaCheckout.models'
import { OrderStatus } from '../../../types/OrderStatus'

interface ReassignedProductsProps {
  order: OrderDto
  programSponsor: string | undefined
}

const ReassignedProducts: React.FC<ReassignedProductsProps> = (props) => {
  const { order, programSponsor } = props

  const history = useHistory()
  const { t } = useTranslation()

  const [expandedRows, setExpandedRows] = useState<string[]>([])
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)

  const reassignedItems: OrderLineDto[] = getReassignedOrderItems(order)

  const handleToggleRow = (itemId: string) => {
    setExpandedRows((prev: string[]) =>
      prev.includes(itemId)
        ? prev.filter((id: string) => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleSelectProduct = (orderLine: OrderLineDto) => {
    history.push(`/programs/access-programs/${orderLine.programId}`)
  }

  const handleMouseEnter = (id: string) => {
    setHoveredRow(id)
  }
  const handleMouseLeave = () => {
    setHoveredRow(null)
  }

  const getProductDetailsColumns = () => [
    {
      name: 'strength',
      viewName: 'Strength',
      translationKey: 'glossary:strength',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <>
          {orderLine.skuCatalogItem?.products.map((product: any) => (
            <StyledStrengthCell key={`strength-item-${product.genericName}`}>
              <ClinText
                variant={TypographyVariant.SmallUI}
                lineHeight={ClinTheme.lineHeights.body}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {product.genericName}
              </ClinText>
              <ClinText
                lineHeight={ClinTheme.lineHeights.body}
                fontSize={ClinTheme.fontSizes[1]}
              >
                {product.strength}
              </ClinText>
            </StyledStrengthCell>
          ))}
        </>
      )
    },
    {
      name: 'dosageFormAndPackSize',
      viewName: 'Dosage form and pack size',
      translationKey: 'glossary:dosage_and_packsize',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <>
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.skuCatalogItem?.item.formulation}
          </ClinText>
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.skuCatalogItem?.item.packSize}
          </ClinText>
        </>
      )
    },
    {
      name: 'status',
      viewName: 'Status',
      translationKey: 'glossary:status',
      width: '100%',
      minWidth: '168px',
      maxWidth: '168px',
      renderValue: () => {
        return (
          <StyledStatusCell>
            <StatusColumn orderStatus={OrderStatus.Reassigned} />
          </StyledStatusCell>
        )
      }
    },
    {
      name: 'quantity',
      viewName: 'Quantity',
      translationKey: 'order_detail:quantity',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => {
        return (
          <ClinText
            fontSize={ClinTheme.fontSizes[1]}
            lineHeight={ClinTheme.lineHeights.body}
          >
            {orderLine.vialsReassignedQuantity ?? '--'}
          </ClinText>
        )
      }
    },
    {
      name: 'toggle',
      viewName: 'Actions',
      translationKey: 'order_detail:actions',
      width: '100%',
      isSortable: false,
      renderValue: (orderLine: OrderLineDto) => (
        <StyledActions onClick={(e) => e.stopPropagation()}>
          <StyledClinTableToggleRowButton
            isToggled={expandedRows.includes(
              `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
            )}
            onClick={() => {
              handleToggleRow &&
                handleToggleRow(
                  `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                )
            }}
          />
        </StyledActions>
      )
    }
  ]

  useEffect(() => {
    const reassignedItems: OrderLineDto[] = getReassignedOrderItems(order)
    const orderLines = reassignedItems.filter((ol) => ol.medicationFlag === 'Y')
    if (orderLines?.length === 1) {
      const orderLine = orderLines[0]
      handleToggleRow(`${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`)
    }
  }, [order])

  if (!order) return null

  const columns = getProductDetailsColumns()

  return (
    <>
      <StyledProductDetailsSection>
        <ClinText
          as="span"
          color={ClinTheme.colors.black}
          fontWeight={ClinTheme.fontWeights.bold}
          fontSize={ClinTheme.fontSizes[1]}
          lineHeight={ClinTheme.lineHeights.heading[1]}
        >
          {t('ma_order_detail:reassigned_products')}
        </ClinText>
      </StyledProductDetailsSection>
      <StyledDesktopWrapper>
        <StyledClinTable
          borderColapseSeparate={true}
          className="TableOverflowVisible"
          tableHeader={() =>
            columns.map((column) => (
              <StyledClinPatientTableHeaderCell
                key={`th-${column.name}`}
                background={ClinTheme.colors.lightGrey}
              >
                <FixedWidthCell
                  width={column.width}
                  minWidth={column.minWidth}
                  maxWidth={column.maxWidth}
                >
                  {column.isSortable ? (
                    <StyledClinTableOrderToggleWrapper>
                      <ClinTableOrderToggle isUppercase={false}>
                        {column.viewName}
                      </ClinTableOrderToggle>
                    </StyledClinTableOrderToggleWrapper>
                  ) : (
                    <StyledClinTableOrderToggleWrapper>
                      <ClinTableOrderToggle hideSortIcon={true}>
                        {column.viewName}
                      </ClinTableOrderToggle>
                    </StyledClinTableOrderToggleWrapper>
                  )}
                </FixedWidthCell>
              </StyledClinPatientTableHeaderCell>
            ))
          }
        >
          {reassignedItems
            ?.filter((ol) => ol.medicationFlag === 'Y')
            ?.map((orderLine: OrderLineDto) => (
              <Fragment
                key={`variant-row-${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`}
              >
                {/* Main Row */}
                <StyledClinTableRow
                  background={ClinTheme.colors.white}
                  className={`main-row ${
                    hoveredRow ===
                    `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                      ? 'highlighted'
                      : ''
                  } ${
                    expandedRows.includes(
                      `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                    )
                      ? 'expanded'
                      : ''
                  }`}
                  onMouseEnter={() =>
                    handleMouseEnter(
                      `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                    )
                  }
                  onMouseLeave={handleMouseLeave}
                >
                  {columns.map((column) => (
                    <StyledClinTableBodyCell
                      key={`${column.name}-${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`}
                      rowExpanded={expandedRows.includes(
                        `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                      )}
                      onClick={() =>
                        handleSelectProduct && handleSelectProduct(orderLine)
                      }
                    >
                      <FixedWidthCell
                        width={column.width}
                        minWidth={column.minWidth}
                        maxWidth={column.maxWidth}
                      >
                        {column.renderValue
                          ? column.renderValue(orderLine)
                          : null}
                      </FixedWidthCell>
                    </StyledClinTableBodyCell>
                  ))}
                </StyledClinTableRow>

                {/* Expanded Row */}
                <StyledClinTableExpandedRow
                  background={ClinTheme.colors.white}
                  isOpen={expandedRows.includes(
                    `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                  )}
                  className={`expanded-row ${
                    hoveredRow ===
                    `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                      ? 'highlighted'
                      : ''
                  }`}
                  onMouseEnter={() =>
                    handleMouseEnter(
                      `${orderLine.lineId}-${orderLine.skuCatalogItem?.sku}`
                    )
                  }
                  onMouseLeave={handleMouseLeave}
                >
                  <StyledClinTableBodyCell
                    colSpan={10}
                    style={{ padding: '16px' }}
                    onClick={() =>
                      handleSelectProduct && handleSelectProduct(orderLine)
                    }
                  >
                    <StyledProductInformationBox>
                      <ProductInformationBox>
                        <ProductInformation
                          isReassigned
                          orderId={order.orderNumber}
                          orderStatus={order.orderStatus}
                          orderLine={orderLine}
                          programSponsor={programSponsor}
                        />
                      </ProductInformationBox>
                    </StyledProductInformationBox>
                  </StyledClinTableBodyCell>
                </StyledClinTableExpandedRow>

                {/* Empty Row */}
                <StyledClinTableRow disabled>
                  <ClinTableBodyCell
                    colSpan={10}
                    style={{ padding: '4px' }}
                  ></ClinTableBodyCell>
                </StyledClinTableRow>
              </Fragment>
            ))}
        </StyledClinTable>
      </StyledDesktopWrapper>
    </>
  )
}

export default ReassignedProducts
